/**
 * Shooter.js
 * Handles the cannon and the shooting of the current bubble
 */

import { CoreEvents, Game, GameAnimatedSprite, GameScene, GameTween, KeyboardKey } from "pixi_core";

import { Bubble } from "./Bubble";
import { BubbleFactory } from "./BubbleFactory";
import { Common } from "./Common";
import { AimLine } from "./AimLine";

import {
    BubbleColor,
    BubblePool,
    ShooterState,
    Heroes,
    CheatEvents,
    GameEvents,
    SceneLayers,
    BuffDebuff,
    Tutorials,
    TutorialStep,
    AIMLINE_POSITION_X_OFFSET,
    BUBBLE_COLOR_NAME_TO_ID,
    RESERVE_SCALE,
    RESERVE_X_POSITION,
    RESERVE_Y_POSITION,
    SHOOTER_X_POSITION,
    SHOOTER_Y_POSITION,
    BUBBLE_HERO_SPAWN_ANIMATION,
    LEVEL_ABILITIES
} from "../Constants";

export class Shooter {
    /**
     * @public
     * @param {BubbleFactory} bubbleFactory
     * @param {GameScene} scene
     */
    constructor(bubbleFactory, scene) {
        Common.shooter = this;

        /** @private @type {BubbleFactory} */
        this._bubbleFactory = bubbleFactory;

        /** @private @type {GameScene} */
        this._scene = scene;

        /** @private @type {Bubble} */
        this._currentBubble = null;

        /** @private @type {Bubble[]} */
        this._reserveBubbles = [];

        /** @private @type {Boolean} */
        this._fillReserveAfterReload = false;

        /** @private @type {Boolean} - Is a reloading or swapping animation currently in progress? */
        this._reloading = false;

        /** @private @type {ShooterState} */
        this._state = null;

        /** @private @type {Number} */
        this._aimAngle = null;

        /** @private @type {GameTween} */
        this._reloadTween = null;

        /** @private @type {GameTween} */
        this._unloadTween = null;

        /** @private @type {GameTween[]} */
        this._reserveTweens = [];

        /** @private @type {GameAnimatedSprite} */
        this._specialBubbleSpawningAnimation = new GameAnimatedSprite(
            null,
            Game.design.getGraphicData(BUBBLE_HERO_SPAWN_ANIMATION),
            this._scene,
            SceneLayers.eBUBBLES
        );
        this._specialBubbleSpawningAnimation.x = SHOOTER_X_POSITION;
        this._specialBubbleSpawningAnimation.y = SHOOTER_Y_POSITION;
        this._specialBubbleSpawningAnimation.visible = false;

        /** @private @type {Number} */
        this._maxAimAngle = Common.settings.maxAimAngle;

        /** @private @type {AimLine} */
        this._aimLine = new AimLine();

        /** @private @type {Boolean} */
        this._aimLineVisible = false;

        /** @private @type {Boolean} Is the last input used a keyboard input?*/
        this._isUsingKeyboard = false;

        /** @private @type {BubbleColor[]} */
        this._availableBubbleColors = [];

        /** @private @type {GameAnimatedSprite[]} */
        this._bubbleSpawnFx = [];

        /** @private @type {Number} */
        this._nbRandomBubbles = 0;

        /** @private @type {GameAnimatedSprite} */
        this._randomizedShooterTimer = new GameAnimatedSprite(
            null,
            Game.design.getGraphicData("RandomizedShooterTimer"),
            this._scene,
            SceneLayers.eBUBBLES
        );
        this._randomizedShooterTimer.x = SHOOTER_X_POSITION;
        this._randomizedShooterTimer.y = SHOOTER_Y_POSITION;
        this._randomizedShooterTimer.visible = false;

        const forcedColorName = Game.windows.cheatsMenu.getValue("ForceBubbleColor");
        /** @public @type {BubbleColor} */
        this._forcedBubbleColor = Game.utils.assertNumber(BUBBLE_COLOR_NAME_TO_ID[forcedColorName], BubbleColor.eNONE);

        this._setShooterState(ShooterState.eBUSY);

        Game.on(CoreEvents.eINPUT_DOWN, this._onInputDown, this);
        Game.on(CoreEvents.eINPUT_MOVE, this._onInputMove, this);
        Game.on(CoreEvents.eINPUT_UP, this._onInputUp, this);
        Game.on(CoreEvents.eINPUT_KEY_DOWN, this._onKeyDown, this);
        Game.on(CoreEvents.eINPUT_KEY_HOLD, this._onKeyHold, this);
        Game.on(CoreEvents.eINPUT_KEY_UP, this._onKeyUp, this);

        Game.on(GameEvents.eGAME_DISABLE_INPUTS, this._onDisableInputs, this);
        Game.on(GameEvents.eGAME_ENABLE_INPUTS, this._onEnableInputs, this);

        Game.on(GameEvents.eCANNON_RELOAD, this._onCannonReload, this);
        Game.on(GameEvents.eCANNON_SWAP, this._swapBubbles, this);
        Game.on(GameEvents.eCANNON_ABILITY_CHANGE_BUBBLE, this._onAbilityChangeBubble, this);

        Game.on(CheatEvents.eFORCE_BUBBLE_COLOR, this._onCheatsForceBubbleColor, this);
    }

    /**
     * @public
     * @param {Number} deltaTime
     */
    updateLoop(deltaTime) {
        if (this._nbRandomBubbles > 0 && this._currentBubble && this._currentBubble.color < BubbleColor.eSPECIAL) {
            if (this._randomizedShooterTimer.visible && !this._randomizedShooterTimer.playing) {
                this._randomizedShooterTimer.playAnimation("loop");
                let color = Math.floor(Math.random() * BubbleColor.eSPECIAL);

                if (color === this._currentBubble.color) {
                    if (this._currentBubble.color >= BubbleColor.ePURPLE) {
                        color = BubbleColor.eGREEN; // Because it's P's favourite colour :P
                    } else {
                        color += 1;
                    }
                }

                this._currentBubble.changeColor({ color: color, special: color }, true);
                this._aimLine.setColor(this._currentBubble.color);
            }
        }

        this._aimLine.updateLoop(deltaTime);
    }

    /**
     * @private
     */
    _onCannonReload() {
        this._setShooterState(ShooterState.eREADY, true);
    }

    /**
     * @private
     */
    _reloadBubble() {
        if (this._currentBubble) return;

        if (this._reserveBubbles.length > 0) {
            this._reloadReserveBubbles();
        } else if (this._fillReserveAfterReload === true) {
            this._fillReserve();
            this._fillReserveAfterReload = false;
        }

        /** @type {BubbleColor} */
        let cannonBubbleColor = this._getRandomCannonBubbleColor(false);
        if (this._forcedBubbleColor !== BubbleColor.eNONE) {
            cannonBubbleColor = this._forcedBubbleColor;
        }

        if (!this._currentBubble) {
            this._currentBubble = this._bubbleFactory.createBubble(
                { x: SHOOTER_X_POSITION, y: SHOOTER_Y_POSITION },
                { color: cannonBubbleColor, special: cannonBubbleColor },
                null,
                BubblePool.eCANNON
            );
            this._currentBubble.spawn(this._onCurrentBubbleSpawned.bind(this), 1, true);
            this._aimLine.setColor(this._currentBubble.color);
            if (this._currentBubble.color < BubbleColor.eSPECIAL && this._nbRandomBubbles > 0) {
                this._randomizedShooterTimer.visible = true;
                this._randomizedShooterTimer.playAnimation("loop");
            } else {
                this._randomizedShooterTimer.visible = false;
                this._randomizedShooterTimer.stop();
                this._currentBubble.showCannonSpin();
            }
        }

        if (this._reserveBubbles.length === 0) {
            // Ideally, reserve bubble should have a different color than one in the cannon
            let reserveBubbleColor = this._getRandomCannonBubbleColor(true);
            if (this._forcedBubbleColor !== BubbleColor.eNONE) {
                reserveBubbleColor = this._forcedBubbleColor;
            }

            let reserveBubble = this._bubbleFactory.createBubble(
                { x: RESERVE_X_POSITION, y: RESERVE_Y_POSITION },
                { color: reserveBubbleColor, special: reserveBubbleColor },
                null,
                BubblePool.eCANNON
            );
            reserveBubble.scale = RESERVE_SCALE;
            this._reserveBubbles.unshift(reserveBubble);
            reserveBubble.spawn(null, RESERVE_SCALE, true);

            Game.fire(GameEvents.eCANNON_RESERVE_CHANGED, this._reserveBubbles.length);

            if (this._nbRandomBubbles > 0) {
                --this._nbRandomBubbles;
                Game.fire(GameEvents.eGAME_SET_BUFF_TIMER, BuffDebuff.eRANDOMIZED_SHOOTER, this._nbRandomBubbles);

                if (this._nbRandomBubbles <= 0) {
                    this._nbRandomBubbles = 0;
                }
            }
        }

        // Fetch pierce bubble count (Captain Marvel)
        if (this._currentBubble.color === BubbleColor.ePIERCE) {
            const heroData = Common.characters.getStats(Heroes.eCAPTAIN_MARVEL);
            this._currentBubble.pierceCount = Game.utils.assertNumber(heroData.PierceCount, 0);
        }
    }

    /**
     * @private
     * @param {Boolean} tryExcludeCurrentColor
     * @returns {BubbleColor}
     */
    _getRandomCannonBubbleColor(tryExcludeCurrentColor) {
        if (this._availableBubbleColors.length > 0) {
            let availableColors = [];
            for (const availableColor of this._availableBubbleColors) {
                let includeColor = true;

                if (
                    tryExcludeCurrentColor &&
                    this._currentBubble &&
                    this._availableBubbleColors.length > 1 &&
                    availableColor === this._currentBubble.color
                ) {
                    includeColor = false;
                }

                if (includeColor) {
                    availableColors.push(availableColor);
                }
            }

            if (availableColors.length > 0) {
                const randomIndex = Math.floor(Math.random() * availableColors.length);
                return availableColors[randomIndex];
            } else {
                return BubbleColor.eRANDOM;
            }
        } else {
            return BubbleColor.eRANDOM;
        }
    }

    /**
     * @private
     */
    _onCurrentBubbleSpawned() {
        if (Common.currentLevel.getLevelNumber() === LEVEL_ABILITIES) {
            Common.tutorial.openTutorial(Tutorials.eLVL_2, TutorialStep.eSWITCH_BUBBLES);
        }
    }

    /**
     * Animates the reserve bubbles' movement when reloading
     * @private
     */
    _reloadReserveBubbles() {
        this._reloading = true;
        this._aimLine.update(false, this._aimAngle);

        let nextCurrentBubble = this._reserveBubbles.shift();
        [this._currentBubble, nextCurrentBubble] = [nextCurrentBubble, this._currentBubble];

        this._destroyTweens();

        Game.fire(GameEvents.eCANNON_RESERVE_CHANGED, this._reserveBubbles.length);

        this._reloadTween = Game.windows
            .getCurrentWindow()
            .createTween(this._currentBubble)
            .to({ x: SHOOTER_X_POSITION, y: SHOOTER_Y_POSITION, scale: 1 }, Common.settings.cannonReloadTime)
            .easing(GameTween.Easing.QuadraticOut)
            .onComplete(() => {
                this._destroyTweens();

                if (this._currentBubble.color < BubbleColor.eSPECIAL && this._nbRandomBubbles > 0) {
                    this._randomizedShooterTimer.visible = true;
                    this._randomizedShooterTimer.playAnimation("loop");
                } else {
                    this._randomizedShooterTimer.visible = false;
                    this._randomizedShooterTimer.stop();
                    this._currentBubble.showCannonSpin();
                }

                this._reloading = false;

                if (this._fillReserveAfterReload === true) {
                    this._fillReserve();
                    this._fillReserveAfterReload = false;
                }

                // Show aim line if using keyboard
                if (this._isUsingKeyboard) {
                    this._aimLineVisible = true;
                    this._setAngle(this._aimAngle);
                }
            });

        this._aimLine.setColor(this._currentBubble.color);

        for (let i = 0; i < this._reserveBubbles.length; ++i) {
            this._reserveBubbles[i].visible = i <= Common.settings.cannonExtraVisibleBubbles;

            const reserveTween = Game.windows
                .getCurrentWindow()
                .createTween(this._reserveBubbles[i])
                .to(
                    { x: RESERVE_X_POSITION, y: RESERVE_Y_POSITION - i * Common.settings.cannonReserveSpacing, scale: RESERVE_SCALE },
                    Common.settings.cannonReloadTime
                )
                .easing(GameTween.Easing.QuadraticOut);

            this._reserveTweens.push(reserveTween);
        }

        for (const tween of this._reserveTweens) {
            tween.start();
        }
        this._reloadTween.start();
    }

    /**
     * The player tapped the swap bubble button or pressed the left/right arrow keys
     * ==> Swap the current loaded bubble with the first reserve bubble
     * @private
     */
    _swapBubbles() {
        if (!this._currentBubble || this._reloading) {
            return;
        }

        if (this._state === ShooterState.eREADY) {
            this._setShooterState(ShooterState.eSWAPPING);

            if (this._randomizedShooterTimer.visible) {
                this._randomizedShooterTimer.visible = false;
                this._randomizedShooterTimer.stop();
            }

            if (Math.random() < 0.5) {
                Game.audio.playSfx("IDSND_BUB_SWITCH_01");
            } else {
                Game.audio.playSfx("IDSND_BUB_SWITCH_02");
            }
        }
    }

    /**
     * The player release the pointer input or tapped the spacebar to shoot
     * ==> Shoot the current loaded bubble in the direction of aim
     * @private
     */
    _shootBubble() {
        if (!this._currentBubble || this._reloading) {
            return;
        }

        if (this._state === ShooterState.eREADY && this._aimLineVisible) {
            if (this._currentBubble && this._currentBubble.color === BubbleColor.ePIERCE) {
                Game.audio.playSfx("IDSND_BUB_SHOT_PIERCE");
            } else {
                Game.audio.playSfx("IDSND_BUB_SHOT");
            }

            this._currentBubble.setPos(SHOOTER_X_POSITION, SHOOTER_Y_POSITION);
            this._setShooterState(ShooterState.eBUSY);
            Game.fire(GameEvents.eCANNON_SHOOT, this._aimAngle, this._currentBubble, true);
            Game.fire(GameEvents.eSWAP_HINT_END);
            this._currentBubble = null;

            if (this._randomizedShooterTimer.visible) {
                this._randomizedShooterTimer.stop();
                this._randomizedShooterTimer.visible = false;
            }
        }
    }

    /**
     * Set the current state of the shooter
     * @private
     * @param {ShooterState} state
     * @param {Boolean} reloading
     */
    _setShooterState(state, reloading = false) {
        if (!reloading && this._state === state) {
            return;
        }

        this._state = state;
        this._setAngle(this._aimAngle);

        switch (state) {
            case ShooterState.eREADY:
                Game.fire(GameEvents.eSWAP_HINT_BEGIN);
                this._reloadBubble();
                break;
            case ShooterState.eSWAPPING:
                this._playSwapBubblesAnimation();
                break;
        }
    }

    /**
     * Plays the swap bubble tweens and returns to the ready state afer they're done
     * @private
     */
    _playSwapBubblesAnimation() {
        this._reloading = true;

        this._destroyTweens();

        this._reloadTween = Game.windows
            .getCurrentWindow()
            .createTween(this._reserveBubbles[0])
            .to({ x: SHOOTER_X_POSITION, y: SHOOTER_Y_POSITION, scale: 1 }, Common.settings.cannonReloadTime)
            .easing(GameTween.Easing.QuadraticOut)
            .start();

        this._currentBubble.hideCannonSpin();
        this._unloadTween = Game.windows
            .getCurrentWindow()
            .createTween(this._currentBubble)
            .to({ x: RESERVE_X_POSITION, y: RESERVE_Y_POSITION, scale: RESERVE_SCALE }, Common.settings.cannonReloadTime)
            .easing(GameTween.Easing.QuadraticOut)
            .onComplete(() => {
                this._finishBubbleSwap();
            })
            .start();
    }

    /**
     * Finishes the bubble swap animation
     * @private
     */
    _finishBubbleSwap() {
        this._destroyTweens();
        let swapBubble = this._reserveBubbles.shift();
        [this._currentBubble, swapBubble] = [swapBubble, this._currentBubble];
        this._reserveBubbles.unshift(swapBubble);
        this._state = ShooterState.eREADY;

        if (this._nbRandomBubbles > 0 && this._currentBubble.color < BubbleColor.eSPECIAL) {
            this._randomizedShooterTimer.visible = true;
        } else {
            this._randomizedShooterTimer.visible = false;
            this._randomizedShooterTimer.stop();
            this._currentBubble.showCannonSpin();
        }
        this._aimLine.setColor(this._currentBubble.color);

        this._reloading = false;

        // Show aim line if using keyboard
        if (this._isUsingKeyboard) {
            this._aimLineVisible = true;
            this._setAngle(this._aimAngle);
        }
    }

    /**
     * Sets the angle of the aim line
     * @private
     */
    _setAngle(angle) {
        if (this._state !== ShooterState.eREADY || this._reloadTween) this._aimLineVisible = false;
        if (this._state === ShooterState.eREADY) {
            this._aimAngle = angle;
            if (this._isUsingKeyboard) this._aimLineVisible = true;
        }
        this._aimLine.update(this._aimLineVisible, this._aimAngle);
    }

    /**
     * Calculate the aim angle required by the screen position of the mouse input or touch
     * @private
     * @param {{x: Number, y: Number}} screenPosition
     */
    _getInputAngle(screenPosition) {
        const screenX = screenPosition.x;
        const screenY = screenPosition.y;

        // Clicked left of the shooter
        if (screenX <= AIMLINE_POSITION_X_OFFSET) {
            this._aimLineVisible = false;
            this._isUsingKeyboard = false;
            this._setAngle(0);
            return;
        }

        const rad = Math.atan(screenY / (screenX - AIMLINE_POSITION_X_OFFSET));
        const angle = (rad / (2 * Math.PI)) * 360;

        if (Math.abs(angle) < this._maxAimAngle) {
            this._isUsingKeyboard = false;
            this._aimLineVisible = true;
            this._setAngle(angle);
        } else {
            this._aimLineVisible = false;
            this._setAngle(0);
        }
    }

    /**
     * On touch or mouse input down
     * @private
     * @param {{x: Number, y: Number}} position
     */
    _onInputDown(position) {
        this._getInputAngle(Game.screenToViewport(position.x, position.y));
    }

    /**
     * On touch or mouse input move
     * @private
     * @param {{x: Number, y: Number}} position
     */
    _onInputMove(position) {
        this._getInputAngle(Game.screenToViewport(position.x, position.y));
    }

    /**
     * On touch or mouse input up
     * @private
     */
    _onInputUp() {
        this._shootBubble();
    }

    /**
     * On keyboard key down
     * @private
     * @param {String} key
     */
    _onKeyDown(key) {
        if (Game.isPaused()) {
            return;
        }

        if (this._state !== ShooterState.eREADY || this._reloadTween) {
            return;
        }

        switch (key) {
            case KeyboardKey.eKEY_ARROW_UP:
                this._onUpArrow();
                break;

            case KeyboardKey.eKEY_ARROW_DOWN:
                this._onDownArrow();
                break;

            case KeyboardKey.eKEY_SPACE:
                this._shootBubble();
                break;

            case KeyboardKey.eKEY_ARROW_LEFT:
                this._swapBubbles();
                break;

            case KeyboardKey.eKEY_ARROW_RIGHT:
                this._swapBubbles();
                break;

            default:
                break;
        }
    }

    /**
     * On keyboard key hold
     * @private
     * @param {String} key
     */
    _onKeyHold(key) {
        switch (key) {
            case KeyboardKey.eKEY_ARROW_UP:
                this._onUpArrow();
                break;

            case KeyboardKey.eKEY_ARROW_DOWN:
                this._onDownArrow();
                break;

            default:
                break;
        }
    }

    /**
     * On keyboard key up arrow
     * @private
     * @param {String} key
     */
    _onKeyUp(key) {}

    /**
     * On keyboard up arrow
     * @private
     */
    _onUpArrow() {
        if (this._state === ShooterState.eREADY && !this._reloadTween) {
            this._isUsingKeyboard = true;
            this._aimLineVisible = true;
            if (this._aimAngle > -this._maxAimAngle) this._setAngle(--this._aimAngle);
        }
    }

    /**
     * On keyboard down
     * @private
     */
    _onDownArrow() {
        if (this._state === ShooterState.eREADY && !this._reloadTween) {
            this._isUsingKeyboard = true;
            this._aimLineVisible = true;
            if (this._aimAngle < this._maxAimAngle) this._setAngle(++this._aimAngle);
        }
    }

    /**
     * @private
     * @param {BubbleColor} specialType
     */
    _onAbilityChangeBubble(specialType) {
        Game.audio.playSfx("IDSND_BUB_ABIL_SPAWN");
        this._currentBubble.hideCannonSpin();

        let oldBubble = null;
        [oldBubble, this._currentBubble] = [this._currentBubble, oldBubble];

        // Save extra bubble
        this._reserveBubbles.unshift(oldBubble);
        this._moveReserveBubbles();

        Game.fire(GameEvents.eCANNON_RESERVE_CHANGED, this._reserveBubbles.length);

        this._specialBubbleSpawningAnimation.visible = true;
        this._specialBubbleSpawningAnimation.playAnimation("preSpawn");
        this._specialBubbleSpawningAnimation.onComplete = () => {
            this._spawnSpecialBubble(specialType);
        };
    }

    /**
     * @private
     * @param {BubbleColor} specialType
     */
    _spawnSpecialBubble(specialType) {
        // Create ability bubble
        this._currentBubble = this._bubbleFactory.createBubble(
            { x: SHOOTER_X_POSITION, y: SHOOTER_Y_POSITION },
            { color: specialType, special: specialType },
            null,
            BubblePool.eCANNON
        );
        this._currentBubble.spawn(null, 1, true);

        this._aimLine.setColor(this._currentBubble.color);
        this._randomizedShooterTimer.visible = false;
        this._randomizedShooterTimer.stop();

        // Fetch pierce bubble count (Captain Marvel)
        if (this._currentBubble.color === BubbleColor.ePIERCE) {
            const heroData = Common.characters.getStats(Heroes.eCAPTAIN_MARVEL);
            this._currentBubble.pierceCount = Game.utils.assertNumber(heroData.PierceCount, 0);
        }

        this._specialBubbleSpawningAnimation.playAnimation("spawn");
        this._specialBubbleSpawningAnimation.onComplete = () => {
            this._specialBubbleSpawningAnimation.visible = false;
        };
    }

    /**
     * Move reserve bubbles to their new position
     * @private
     */
    _moveReserveBubbles() {
        this._reloading = true;

        for (let i = 0; i < this._reserveBubbles.length; ++i) {
            this._reserveBubbles[i].visible = i <= Common.settings.cannonExtraVisibleBubbles;

            const reserveTween = Game.windows
                .getCurrentWindow()
                .createTween(this._reserveBubbles[i])
                .to(
                    { x: RESERVE_X_POSITION, y: RESERVE_Y_POSITION - i * Common.settings.cannonReserveSpacing, scale: RESERVE_SCALE },
                    Common.settings.cannonReloadTime
                )
                .easing(GameTween.Easing.QuadraticOut)
                .onComplete(() => {
                    this._reloading = false;
                });

            this._reserveTweens.push(reserveTween);
        }

        for (const tween of this._reserveTweens) {
            tween.start();
        }
    }

    /**
     * @public
     * @param {BubbleColor[]} bubbleColors
     */
    setAvailableColors(bubbleColors) {
        this._availableBubbleColors = bubbleColors;
    }

    /**
     * @private
     * @returns {BubbleColor}
     */
    _getNewBubbleColor() {
        /** @private @type {BubbleColor} */
        let color = BubbleColor.eRANDOM;

        if (this._availableBubbleColors.length > 0) {
            color = this._availableBubbleColors[Math.floor(Math.random() * this._availableBubbleColors.length)];
        }

        return this._forcedBubbleColor !== BubbleColor.eNONE ? this._forcedBubbleColor : color;
    }

    /**
     * Add random bubbles as part of the RandomShooter enemy ability
     * @public
     * @param {Number} nbBubbles
     */
    addRandomBubbles(nbBubbles) {
        Game.audio.playSfx("IDSND_RANDOM_SHOOTER_DEBUFF");
        this._nbRandomBubbles += nbBubbles;

        if (this._currentBubble) {
            this._currentBubble.changeColor({ color: Math.floor(Math.random() * BubbleColor.eSPECIAL), special: BubbleColor.eNONE }, true);
            --this._nbRandomBubbles;
            this._randomizedShooterTimer.visible = true;

            this._aimLine.setColor(this._currentBubble.color);
        }

        Game.fire(GameEvents.eGAME_SET_RANDOM_BUBBLES_COUNTER, this._nbRandomBubbles);
        ++this._nbRandomBubbles;
    }

    /**
     * Fill the reserve with bubbles after the next reload
     * @public
     */
    fillReserveAfterReload() {
        this._fillReserveAfterReload = true;
    }

    /**
     * Fill the reserve with bubbles
     * @private
     */
    _fillReserve() {
        const nbBubbles = Common.settings.cannonExtraVisibleBubbles - this._reserveBubbles.length;
        if (nbBubbles < 1) {
            return;
        }

        this._reloading = true;
        Game.audio.playSfx("IDSND_BUB_ABIL_SPAWN");

        Game.fire(GameEvents.eCANNON_RESERVE_CHANGED, this._reserveBubbles.length + nbBubbles);

        const graphicData = Game.design.getGraphicData(BUBBLE_HERO_SPAWN_ANIMATION);
        for (let i = 0; i < nbBubbles; ++i) {
            const fx = new GameAnimatedSprite(null, graphicData, this._scene, SceneLayers.eBUBBLES);
            this._bubbleSpawnFx.push(fx);

            const reserveX = RESERVE_X_POSITION;
            const reserveY = RESERVE_Y_POSITION - (i + this._reserveBubbles.length) * Common.settings.cannonReserveSpacing;
            fx.x = reserveX;
            fx.y = reserveY;
            fx.scale.x = RESERVE_SCALE;
            fx.scale.y = RESERVE_SCALE;

            fx.playAnimation("preSpawn");
            fx.onComplete = () => {
                this._spawnReserveBubble(reserveX, reserveY, fx);
            };
        }
    }

    /**
     * Fill the reserve with bubbles
     * @private
     * @param {Number} reserveX
     * @param {Number} reserveY
     * @param {GameAnimatedSprite} fx
     */
    _spawnReserveBubble(reserveX, reserveY, fx) {
        const color = this._getNewBubbleColor();

        let reserveBubble = this._bubbleFactory.createBubble(
            { x: reserveX, y: reserveY },
            { color: color, special: color },
            null,
            BubblePool.eCANNON
        );

        reserveBubble.scale = RESERVE_SCALE;
        reserveBubble.spawn(null, RESERVE_SCALE, false);

        this._reserveBubbles.push(reserveBubble);

        fx.playAnimation("spawn");
        fx.onComplete = () => {
            if (!fx.destroyed) {
                fx.destroy();
            }
        };

        this._reloading = false;
    }

    /**
     * @private
     */
    _onDisableInputs() {
        this._setShooterState(ShooterState.eBUSY);
    }

    /**
     * @private
     */
    _onEnableInputs() {
        this._state = ShooterState.eREADY;

        // Show aim line if using keyboard
        if (!this._reloadTween && this._isUsingKeyboard) {
            this._aimLineVisible = true;
            this._setAngle(this._aimAngle);
        }
    }

    /**
     * @private
     * @param {String} colorName
     */
    _onCheatsForceBubbleColor(colorName) {
        this._forcedBubbleColor = Game.utils.assertNumber(BUBBLE_COLOR_NAME_TO_ID[colorName], BubbleColor.eNONE);

        if (this._currentBubble) {
            let reserveLength = this._reserveBubbles.length;
            for (let i = 0; i < reserveLength; ++i) {
                // Change reserve bubble
                this._currentBubble.destroy();
                this._currentBubble = null;
                this._reloadBubble();
            }

            // Change current bubble
            this._currentBubble.destroy();
            this._currentBubble = null;
            this._reloadBubble();
        }
    }

    /**
     * Destroy all tweens
     * @private
     */
    _destroyTweens() {
        if (this._reloadTween) {
            this._reloadTween.destroy();
            this._reloadTween = null;
        }

        if (this._unloadTween) {
            this._unloadTween.destroy();
            this._unloadTween = null;
        }

        for (const tween of this._reserveTweens) {
            tween.destroy();
        }
        this._reserveTweens = [];
    }

    /**
     * Clear used references
     * @public
     */
    destroy() {
        Common.shooter = null;

        if (this._randomizedShooterTimer) {
            this._randomizedShooterTimer.destroy();
            this._randomizedShooterTimer = null;
        }

        if (this._aimLine) {
            this._aimLine.destroy();
            this._aimLine = null;
        }

        if (this._currentBubble) {
            this._currentBubble.destroy();
            this._currentBubble = null;
        }

        if (this._specialBubbleSpawningAnimation) {
            this._specialBubbleSpawningAnimation.destroy();
            this._specialBubbleSpawningAnimation = null;
        }

        for (const fx of this._bubbleSpawnFx) {
            fx.destroy();
        }
        this._bubbleSpawnFx = [];

        for (const bubble of this._reserveBubbles) {
            if (bubble) {
                bubble.destroy();
            }
        }
        this._reserveBubbles = [];

        this._destroyTweens();

        Game.off(CoreEvents.eINPUT_DOWN, this._onInputDown, this);
        Game.off(CoreEvents.eINPUT_MOVE, this._onInputMove, this);
        Game.off(CoreEvents.eINPUT_UP, this._onInputUp, this);
        Game.off(CoreEvents.eINPUT_KEY_DOWN, this._onKeyDown, this);
        Game.off(CoreEvents.eINPUT_KEY_HOLD, this._onKeyHold, this);
        Game.off(CoreEvents.eINPUT_KEY_UP, this._onKeyUp, this);

        Game.off(GameEvents.eGAME_DISABLE_INPUTS, this._onDisableInputs, this);
        Game.off(GameEvents.eGAME_ENABLE_INPUTS, this._onEnableInputs, this);

        Game.off(GameEvents.eCANNON_RELOAD, this._onCannonReload, this);
        Game.off(GameEvents.eCANNON_SWAP, this._swapBubbles, this);
        Game.off(GameEvents.eCANNON_ABILITY_CHANGE_BUBBLE, this._onAbilityChangeBubble, this);

        Game.off(CheatEvents.eFORCE_BUBBLE_COLOR, this._onCheatsForceBubbleColor, this);
    }
}
